/**
 * TrainingModeContext.tsx
 *
 * Вариант B — «Полная имитация»:
 * - training mode должен быть глобальным для всех операторских страниц
 * - состояние сохраняется в sessionStorage
 * - при включенном режиме весь UI должен видеть только training данные
 *
 * ВАЖНО (практическая интеграция с текущей кодовой базой):
 * В проекте уже есть компоненты, которые делают прямые fetch('/api/requests') и fetch('/api/signals').
 * Чтобы обеспечить изоляцию данных без тотального рефакторинга этих компонентов,
 * добавлен "compatibility-layer": в training mode глобально перехватываем fetch()
 * и переадресуем /api/requests, /api/signals на /api/training/*.
 *
 * Дополнительно:
 * - опасные production-операции (confirmations, link/dismiss signal) в training mode блокируются
 *   или симулируются через training endpoints (без отправки реальных писем).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { BackendService } from '../services/BackendService';

// Интерфейс статистики тренировочного режима
interface TrainingStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  completedTests: number;
  rejectedRequests: number;
  expiredRequests?: number;
  totalSignals: number;
  testSignals: number;
  distressSignals: number;
  pollSignals: number;
  lastActivity: Date | null;
}

// Интерфейс контекста
interface TrainingModeContextType {
  isTrainingMode: boolean;

  toggleTrainingMode: () => void;
  enableTrainingMode: () => void;
  disableTrainingMode: () => void;

  stats: TrainingStats | null;
  loadStats: () => Promise<void>;
  isLoading: boolean;

  resetData: () => Promise<void>;
  seedData: () => Promise<void>;

  // Устаревшее (для обратной совместимости)
  mockData: Record<string, unknown>;
}

// Начальное значение статистики
const initialStats: TrainingStats = {
  totalRequests: 0,
  pendingRequests: 0,
  approvedRequests: 0,
  completedTests: 0,
  rejectedRequests: 0,
  expiredRequests: 0,
  totalSignals: 0,
  testSignals: 0,
  distressSignals: 0,
  pollSignals: 0,
  lastActivity: null,
};

// Создаём контекст
const TrainingModeContext = createContext<TrainingModeContextType>({
  isTrainingMode: false,
  toggleTrainingMode: () => {},
  enableTrainingMode: () => {},
  disableTrainingMode: () => {},
  stats: null,
  loadStats: async () => {},
  isLoading: false,
  resetData: async () => {},
  seedData: async () => {},
  mockData: {},
});

// Ключ для sessionStorage (основной)
const TRAINING_MODE_KEY = 'ssto_training_mode';

// (опционально) shared-ключ для localStorage для сценария «новая вкладка»
// sessionStorage в стандартных браузерах не общий между вкладками.
// Этот shared-key используется только как «мост» и не меняет основного источника истины (sessionStorage).
const TRAINING_MODE_SHARED_KEY = 'ssto_training_mode_shared';
const TRAINING_MODE_SHARED_TTL_MS = 12 * 60 * 60 * 1000; // 12 часов

function safeReadSessionMode(): boolean {
  try {
    return sessionStorage.getItem(TRAINING_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

function safeReadSharedMode(): boolean {
  try {
    const raw = localStorage.getItem(TRAINING_MODE_SHARED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { enabled?: boolean; ts?: number } | null;
    if (!parsed?.enabled || typeof parsed.ts !== 'number') return false;
    if (Date.now() - parsed.ts > TRAINING_MODE_SHARED_TTL_MS) {
      localStorage.removeItem(TRAINING_MODE_SHARED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function safeWriteSharedMode(enabled: boolean): void {
  try {
    if (!enabled) {
      localStorage.removeItem(TRAINING_MODE_SHARED_KEY);
      return;
    }
    localStorage.setItem(
      TRAINING_MODE_SHARED_KEY,
      JSON.stringify({ enabled: true, ts: Date.now() })
    );
  } catch {
    // ignore
  }
}

function getAuthTokenForFetch(): string | null {
  try {
    return (
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      null
    );
  } catch {
    return null;
  }
}

function ensureAuthHeaders(headers?: HeadersInit): Headers {
  const h = new Headers(headers || {});
  const token = getAuthTokenForFetch();
  if (token && !h.has('Authorization')) {
    h.set('Authorization', `Bearer ${token}`);
  }
  return h;
}

function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json');
}

function jsonResponse(payload: unknown, status = 200): Response {
  return new Response(JSON.stringify(payload), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

// ----------------------------------------------------------------------------
// DEBUG (отключаемый):
// 1) Vite env: VITE_DEBUG_FETCH_ROUTER=true
// 2) или вручную в браузере: localStorage.setItem('debugFetchRouter', '1')
// ----------------------------------------------------------------------------
const DEBUG_FETCH_ROUTER = (() => {
  try {
    const envFlag = (import.meta as any)?.env?.VITE_DEBUG_FETCH_ROUTER;
    if (String(envFlag).toLowerCase() === 'true') return true;
    return localStorage.getItem('debugFetchRouter') === '1';
  } catch {
    return false;
  }
})();

function debugLog(...args: any[]) {
  if (DEBUG_FETCH_ROUTER) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

// ИСПРАВЛЕНО: утилиты для надежного переноса body/headers при rewrite fetch (включая edge-case с не-enumerable init)
type AnyRequestInit = RequestInit & Record<string, any>;

function safeCloneRequestBody(req: Request): RequestInit['body'] {
  try {
    // clone() бросит ошибку, если body уже использован/задет
    return req.clone().body;
  } catch {
    return req.body;
  }
}

function bodyPreviewForLog(body: RequestInit['body'] | undefined): string | null {
  if (body == null) return body === null ? 'null' : null;

  if (typeof body === 'string') {
    const trimmed = body.length > 800 ? `${body.slice(0, 800)}…(len=${body.length})` : body;
    return trimmed;
  }

  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    const s = body.toString();
    return s.length > 800 ? `${s.slice(0, 800)}…(len=${s.length})` : s;
  }

  // Blob/FormData/ReadableStream — не читаем напрямую (чтобы не расходовать поток)
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return `Blob(type=${body.type || 'unknown'}, size=${body.size})`;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const keys = Array.from(body.keys());
    const head = keys.slice(0, 10).join(', ');
    return keys.length > 10
      ? `FormData(keys=${head}, … total=${keys.length})`
      : `FormData(keys=${head})`;
  }

  // ReadableStream detection (best-effort)
  if (typeof (body as any)?.getReader === 'function') {
    return 'ReadableStream(body)';
  }

  try {
    return Object.prototype.toString.call(body);
  } catch {
    return 'unknown-body';
  }
}

// ИСПРАВЛЕНО: собираем один Request для originalFetch, явно прокидываем body/headers и заполняем пропущенные поля из baseRequest
function buildRewrittenRequest(
  rewrittenUrl: string,
  baseRequest: Request | undefined,
  init: RequestInit | undefined,
  method: string
): Request {
  const rawInit: any = init ?? {};

  // Берем enumerable поля (для кастомных опций), но ключевые поля ниже проставляем явно
  const nextInit: AnyRequestInit = init ? { ...(rawInit as AnyRequestInit) } : {};

  // method всегда явный и нормализованный
  nextInit.method = method;

  // headers: предпочитаем init.headers, иначе берем из baseRequest
  // ВАЖНО: гарантируем Authorization, даже если исходный fetch был без headers
  const headers = rawInit.headers ?? baseRequest?.headers;
  nextInit.headers = ensureAuthHeaders(headers);

  // Заполняем прочие поля из init (явно, на случай если init — не plain object и spread не перенес)
  if (init) {
    if (rawInit.credentials !== undefined) nextInit.credentials = rawInit.credentials;
    if (rawInit.cache !== undefined) nextInit.cache = rawInit.cache;
    if (rawInit.mode !== undefined) nextInit.mode = rawInit.mode;
    if (rawInit.redirect !== undefined) nextInit.redirect = rawInit.redirect;
    if (rawInit.referrer !== undefined) nextInit.referrer = rawInit.referrer;
    if (rawInit.referrerPolicy !== undefined) nextInit.referrerPolicy = rawInit.referrerPolicy;
    if (rawInit.integrity !== undefined) nextInit.integrity = rawInit.integrity;
    if (rawInit.keepalive !== undefined) nextInit.keepalive = rawInit.keepalive;
    if (rawInit.signal !== undefined) nextInit.signal = rawInit.signal;

    // Некоторые окружения (не браузер) требуют duplex для стримов — прокидываем, если есть
    if (rawInit.duplex !== undefined) (nextInit as any).duplex = rawInit.duplex;
  }

  // Если есть baseRequest — дополняем тем, чего нет в init
  if (baseRequest) {
    if (nextInit.credentials === undefined) nextInit.credentials = baseRequest.credentials;
    if (nextInit.cache === undefined) nextInit.cache = baseRequest.cache;
    if (nextInit.mode === undefined) nextInit.mode = baseRequest.mode;
    if (nextInit.redirect === undefined) nextInit.redirect = baseRequest.redirect;
    if (nextInit.referrer === undefined) nextInit.referrer = baseRequest.referrer;
    if (nextInit.referrerPolicy === undefined) nextInit.referrerPolicy = baseRequest.referrerPolicy;
    if (nextInit.integrity === undefined) nextInit.integrity = baseRequest.integrity;
    if (nextInit.keepalive === undefined) nextInit.keepalive = baseRequest.keepalive;
    if (nextInit.signal === undefined) nextInit.signal = baseRequest.signal;
  }

  // Body rules: GET/HEAD не должны иметь body
  if (method === 'GET' || method === 'HEAD') {
    if ('body' in nextInit) {
      delete (nextInit as any).body;
    }
    return new Request(rewrittenUrl, nextInit);
  }

  // ИСПРАВЛЕНО: явное извлечение body из init или baseRequest (не полагаемся на spread)
  const initHasBody = !!init && Object.prototype.hasOwnProperty.call(init as any, 'body');

  if (initHasBody) {
    (nextInit as any).body = (init as any).body;
  } else if (baseRequest) {
    (nextInit as any).body = safeCloneRequestBody(baseRequest);
  }

  return new Request(rewrittenUrl, nextInit);
}

function normalizeTrainingRequest(req: any): any {
  if (!req || typeof req !== 'object') return req;
  return {
    ...req,
    // Подхватываем ожидаемое UI поле (в некоторых местах используется ssas_number)
    ssas_number: req.ssas_number ?? req.terminal_number ?? req.mmsi ?? null,
  };
}

function normalizeTrainingRequestsPayload(payload: any): any {
  if (Array.isArray(payload)) {
    return payload.map(normalizeTrainingRequest);
  }
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) {
      const items = payload.data.map(normalizeTrainingRequest);
      return {
        ...payload,
        data: items,
        items, // дополнительное поле для совместимости с UI, где ожидается items
      };
    }
    return normalizeTrainingRequest(payload);
  }
  return payload;
}

function normalizeTrainingSignal(signal: any): any {
  if (!signal || typeof signal !== 'object') return signal;

  const lat =
    signal.latitude ?? signal.coordinates?.lat ?? signal.coordinates?.latitude ?? undefined;

  const lon =
    signal.longitude ??
    signal.coordinates?.lon ??
    signal.coordinates?.lng ??
    signal.coordinates?.longitude ??
    undefined;

  const linked = signal.linked_request_id ?? signal.request_id ?? null;
  const status = signal.status ?? (linked ? 'MATCHED' : 'UNMATCHED');

  const coordsObj =
    signal.coordinates ??
    (typeof lat === 'number' && typeof lon === 'number'
      ? {
          lat,
          lon,
          lng: lon,
          latitude: lat,
          longitude: lon,
          raw: `${lat}, ${lon}`,
        }
      : undefined);

  return {
    ...signal,
    status,
    request_id: signal.request_id ?? linked ?? null,
    detection_time: signal.detection_time ?? signal.received_at ?? signal.created_at ?? null,
    coordinates: coordsObj,
  };
}

function normalizeTrainingSignalsPayload(payload: any): any {
  if (Array.isArray(payload)) {
    const items = payload.map(normalizeTrainingSignal);
    return { items, data: items, total: items.length };
  }
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) {
      const items = payload.data.map(normalizeTrainingSignal);
      return {
        ...payload,
        data: items,
        items,
        total: typeof payload.total === 'number' ? payload.total : items.length,
      };
    }
    return normalizeTrainingSignal(payload);
  }
  return payload;
}

/**
 * Преобразование TrainingStats (/api/training/stats) → shape SignalStats (ожидается существующим UI)
 * Это нужно, чтобы старые компоненты, которые дергают /api/signals/stats, не показывали production.
 */
function toSignalStatsFromTrainingStats(trainingStats: any) {
  const totalSignals = Number(trainingStats?.totalSignals ?? 0) || 0;
  const testSignals = Number(trainingStats?.testSignals ?? 0) || 0;
  const distressSignals = Number(trainingStats?.distressSignals ?? 0) || 0;
  const pollSignals = Number(trainingStats?.pollSignals ?? 0) || 0;
  const completedTests = Number(trainingStats?.completedTests ?? 0) || 0;

  return {
    total: totalSignals,
    matched: 0,
    unmatched: totalSignals,
    allTime: {
      total: totalSignals,
      matched: 0,
      confirmed: completedTests,
      unmatched: totalSignals,
    },
    byStatus: {
      MATCHED: 0,
      UNMATCHED: totalSignals,
      ERROR: 0,
    },
    byType: {
      // UI ожидает эти ключи (из SignalMonitor.tsx)
      SSAS_TEST: testSignals,
      EPIRB: distressSignals,
      VHF_DSC: pollSignals,
    },
  };
}

type FetchFn = typeof window.fetch;

interface TrainingFetchRouterCtx {
  originalFetch: FetchFn;
  confirmationToRequest: Map<string, string>;
  confirmations: Map<string, any>;
}

/**
 * В training mode маршрутизируем legacy вызовы:
 * - /api/requests → /api/training/requests
 * - /api/signals  → /api/training/signals
 *
 * + симуляция/блокировка опасных production операций.
 */
async function routeFetchInTrainingMode(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  ctx: TrainingFetchRouterCtx
): Promise<Response> {
  const baseRequest = input instanceof Request ? input : undefined;

  const rawUrl =
    typeof input === 'string' ? input : input instanceof URL ? input.toString() : baseRequest?.url;

  if (!rawUrl) {
    return ctx.originalFetch(input as any, init);
  }

  let url: URL;
  try {
    url = new URL(rawUrl, window.location.origin);
  } catch {
    // Нестандартный URL — не трогаем
    return ctx.originalFetch(input as any, init);
  }

  const pathname = url.pathname;
  const method = (init?.method || baseRequest?.method || 'GET').toUpperCase();

  // Никогда не трогаем training API напрямую
  if (pathname.startsWith('/api/training')) {
    return ctx.originalFetch(input as any, init);
  }

  // ---------------------------------------------------------------------------
  // 1) CONFIRMATIONS: в training mode НЕ ДОЛЖНЫ уходить реальные письма / production workflow
  //    Симулируем prepare/send/reject через PATCH /api/training/requests/:id
  // ---------------------------------------------------------------------------

  // preview → возвращаем 404, чтобы UI применил fallback-генерацию HTML локально
  if (method === 'POST' && /^\/api\/confirmations\/preview\/\d+$/.test(pathname)) {
    return jsonResponse({ success: false, message: 'Preview blocked in training mode' }, 404);
  }

  // prepare/:requestId → отдаём фиктивный confirmation.id
  const prepareMatch = pathname.match(/^\/api\/confirmations\/prepare\/(\d+)$/);
  if (method === 'POST' && prepareMatch) {
    const requestId = prepareMatch[1];
    const confirmationId = `training-${requestId}-${Date.now()}`;
    ctx.confirmationToRequest.set(confirmationId, requestId);

    // Сохраняем в in-memory store (для совместимости с экранами/сервисами, которые читают confirmations)
    try {
      ctx.confirmations.set(confirmationId, {
        id: confirmationId,
        request_id: Number(requestId),
        status: 'ready',
        created_at: new Date().toISOString(),
      });
    } catch {
      // ignore
    }

    return jsonResponse(
      {
        success: true,
        training: true,
        confirmation: { id: confirmationId },
        message: 'Training confirmation prepared (simulated)',
      },
      200
    );
  }

  // :confirmationId/send → PATCH training request → completed
  const sendMatch = pathname.match(/^\/api\/confirmations\/([^/]+)\/send$/);
  if (method === 'POST' && sendMatch) {
    const confirmationId = sendMatch[1];

    const mapped = ctx.confirmationToRequest.get(confirmationId) || null;

    // FIX: если confirmationId пришёл как цифра (например "5"), трактуем как requestId
    const numeric = /^\d+$/.test(confirmationId) ? confirmationId : null;

    // fallback: "training-<requestId>-<ts>"
    const fromTrainingPattern =
      !numeric && confirmationId.includes('-') ? confirmationId.split('-')[1] : null;

    const requestId = mapped || numeric || fromTrainingPattern || null;

    if (!requestId) {
      return jsonResponse(
        { success: false, message: 'Unknown confirmation id (training mode)' },
        404
      );
    }

    const patchUrl = `/api/training/requests/${encodeURIComponent(requestId)}`;
    const patchInit: RequestInit = {
      method: 'PATCH',
      credentials: init?.credentials ?? 'include',
      headers: ensureAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status: 'completed' }),
    };

    const patchRes = await ctx.originalFetch(patchUrl, patchInit);
    if (!patchRes.ok) {
      const txt = await patchRes.text().catch(() => '');
      return jsonResponse(
        { success: false, message: `Training update failed: ${patchRes.status} ${txt}` },
        500
      );
    }

    // Обновляем in-memory store
    try {
      const patchedRequest = await patchRes.json().catch(() => null);
      ctx.confirmations.set(confirmationId, {
        id: confirmationId,
        request_id: Number(requestId),
        status: 'sent',
        sent_at: new Date().toISOString(),
        sent_by: 'training-mode',
        request: patchedRequest ?? { id: Number(requestId), status: 'completed' },
      });
    } catch {
      // ignore
    }

    return jsonResponse(
      {
        success: true,
        training: true,
        message: 'Training confirmation sent (simulated). Request marked as completed.',
      },
      200
    );
  }

  // :requestId/reject → PATCH training request → rejected
  const rejectMatch = pathname.match(/^\/api\/confirmations\/(\d+)\/reject$/);
  if (method === 'POST' && rejectMatch) {
    const requestId = rejectMatch[1];

    const patchUrl = `/api/training/requests/${encodeURIComponent(requestId)}`;
    const patchInit: RequestInit = {
      method: 'PATCH',
      credentials: init?.credentials ?? 'include',
      headers: ensureAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status: 'rejected' }),
    };

    const patchRes = await ctx.originalFetch(patchUrl, patchInit);
    if (!patchRes.ok) {
      const txt = await patchRes.text().catch(() => '');
      return jsonResponse(
        { success: false, message: `Training reject failed: ${patchRes.status} ${txt}` },
        500
      );
    }

    return jsonResponse(
      {
        success: true,
        training: true,
        message: 'Training reject simulated. Request marked as rejected.',
      },
      200
    );
  }

  // GET /api/confirmations → список тренировочных подтверждений (stub)
  if (method === 'GET' && pathname === '/api/confirmations') {
    const items = Array.from(ctx.confirmations.values());
    items.sort((a: any, b: any) => {
      const at = a?.created_at ? Date.parse(a.created_at) : 0;
      const bt = b?.created_at ? Date.parse(b.created_at) : 0;
      return bt - at;
    });
    return jsonResponse(items, 200);
  }

  // GET /api/confirmations/:id → одно подтверждение (stub)
  const confirmationGetMatch = pathname.match(/^\/api\/confirmations\/([^/]+)$/);
  if (method === 'GET' && confirmationGetMatch) {
    const confirmationId = decodeURIComponent(confirmationGetMatch[1]);
    const item = ctx.confirmations.get(confirmationId);
    if (!item) {
      return jsonResponse(
        { success: false, message: 'Confirmation not found (training mode)' },
        404
      );
    }
    return jsonResponse(item, 200);
  }

  // Любые прочие confirmations endpoints в training mode блокируем
  // (возвращаем 404 вместо 423, чтобы UI не интерпретировал это как "lock/blocked" ошибку)
  if (pathname.startsWith('/api/confirmations')) {
    return jsonResponse(
      { success: false, message: 'Confirmations endpoint is not available in training mode' },
      404
    );
  }

  // ---------------------------------------------------------------------------
  // 2) Опасные операции по сигналам (link/dismiss) — блокируем в training mode
  // ---------------------------------------------------------------------------
  if (
    /^\/api\/signals\/[^/]+\/link(\/.*)?$/.test(pathname) ||
    /^\/api\/signals\/[^/]+\/dismiss(\/.*)?$/.test(pathname)
  ) {
    return jsonResponse(
      { success: false, message: 'Signal link/dismiss is blocked in training mode' },
      423
    );
  }

  // ---------------------------------------------------------------------------
  // 3) stats: старый UI может дергать /api/signals/stats или /api/signals/statistics
  //    → перенаправляем на /api/training/stats и конвертируем shape
  // ---------------------------------------------------------------------------
  if (
    method === 'GET' &&
    (pathname === '/api/signals/stats' || pathname === '/api/signals/statistics')
  ) {
    const res = await ctx.originalFetch('/api/training/stats', {
      method: 'GET',
      credentials: init?.credentials ?? 'include',
      headers: ensureAuthHeaders(init?.headers),
    });

    if (!res.ok) return res;

    if (!isJsonResponse(res)) {
      const txt = await res.text().catch(() => '');
      return jsonResponse({ success: false, message: `Unexpected stats response: ${txt}` }, 500);
    }

    const trainingStats = await res.json().catch(() => null);
    return jsonResponse(toSignalStatsFromTrainingStats(trainingStats), 200);
  }

  // ---------------------------------------------------------------------------
  // 4) requests/signals: авто-переадресация на training API
  // ---------------------------------------------------------------------------

  // requests status: /api/requests/:id/status  (используется в UI как reject fallback)
  const requestStatusMatch = pathname.match(/^\/api\/requests\/(\d+)\/status$/);
  if (requestStatusMatch) {
    const requestId = requestStatusMatch[1];

    // Training controller принимает PATCH (а UI может слать PUT) — нормализуем
    const rewrittenMethod = method === 'PUT' ? 'PATCH' : method;

    // Переводим на PATCH /api/training/requests/:id
    url.pathname = `/api/training/requests/${encodeURIComponent(requestId)}`;
    const rewrittenUrl = url.toString();

    const rewrittenRequest = buildRewrittenRequest(
      rewrittenUrl,
      baseRequest,
      init,
      rewrittenMethod
    );
    const res = await ctx.originalFetch(rewrittenRequest);

    // Нормализация как для requests
    if (res.ok && isJsonResponse(res) && res.status !== 204) {
      const payload = await res.json().catch(() => null);
      return jsonResponse(normalizeTrainingRequestsPayload(payload), res.status);
    }

    return res;
  }

  // requests: /api/requests и /api/requests/:id
  if (pathname === '/api/requests' || /^\/api\/requests\/\d+$/.test(pathname)) {
    url.pathname = pathname.replace('/api/requests', '/api/training/requests');
    const rewrittenUrl = url.toString();

    // PUT -> PATCH (training controller использует PATCH)
    const rewrittenMethod = method === 'PUT' ? 'PATCH' : method;

    // ИСПРАВЛЕНО: явно извлекаем body из init/baseRequest и передаем одним Request (без конфликтов Request+init)
    const rewrittenRequest = buildRewrittenRequest(
      rewrittenUrl,
      baseRequest,
      init,
      rewrittenMethod
    );

    // ИСПРАВЛЕНО: отладочное логирование для POST запросов (чтобы видеть body, которое реально уходит в training API)
    if (rewrittenMethod === 'POST') {
      const initHasBody = !!init && Object.prototype.hasOwnProperty.call(init as any, 'body');
      const initBody = initHasBody ? (init as any).body : undefined;

      debugLog('[TrainingMode fetch-router] POST /api/requests -> /api/training/requests', {
        originalUrl: rawUrl,
        rewrittenUrl,
        method: rewrittenMethod,
        initProvided: !!init,
        initHasBody,
        initBodyPreview: bodyPreviewForLog(initBody),
        baseRequestProvided: !!baseRequest,
        baseRequestBodyUsed: baseRequest?.bodyUsed ?? false,
        contentType: rewrittenRequest.headers.get('content-type') || null,
      });

      // Дополнительно: читаем body из clone() — только в debug режиме
      if (DEBUG_FETCH_ROUTER) {
        try {
          const cloned = rewrittenRequest.clone();
          cloned
            .text()
            .then((txt) => {
              const preview = txt.length > 800 ? `${txt.slice(0, 800)}…(len=${txt.length})` : txt;
              debugLog('[TrainingMode fetch-router] POST body (clone preview):', preview);
            })
            .catch((e) => {
              debugLog('[TrainingMode fetch-router] POST body (clone) read failed:', e);
            });
        } catch (e) {
          debugLog('[TrainingMode fetch-router] POST body clone failed:', e);
        }
      }
    }

    const res = await ctx.originalFetch(rewrittenRequest);

    // Нормализуем JSON (добавляем items, ssas_number)
    if (res.ok && isJsonResponse(res) && res.status !== 204) {
      const payload = await res.json().catch(() => null);
      return jsonResponse(normalizeTrainingRequestsPayload(payload), res.status);
    }

    return res;
  }

  // signals: /api/signals и /api/signals/:id
  if (pathname === '/api/signals' || /^\/api\/signals\/\d+$/.test(pathname)) {
    url.pathname = pathname.replace('/api/signals', '/api/training/signals');
    const rewrittenUrl = url.toString();

    // ИСПРАВЛЕНО: делаем rewrite через единый Request, чтобы не терять body на нестандартных init/baseRequest
    const rewrittenRequest = buildRewrittenRequest(rewrittenUrl, baseRequest, init, method);

    const res = await ctx.originalFetch(rewrittenRequest);

    // Нормализуем JSON: items/data + coordinates/request_id/detection_time/status
    if (res.ok && isJsonResponse(res) && res.status !== 204) {
      const payload = await res.json().catch(() => null);

      const normalized = normalizeTrainingSignalsPayload(payload);

      // Совместимость: часть legacy UI ожидает массив от /api/signals (и вызывает .find())
      if (pathname === '/api/signals') {
        const arr = Array.isArray(normalized)
          ? normalized
          : Array.isArray((normalized as any)?.items)
            ? (normalized as any).items
            : Array.isArray((normalized as any)?.data)
              ? (normalized as any).data
              : [];
        return jsonResponse(arr, res.status);
      }

      return jsonResponse(normalized, res.status);
    }

    return res;
  }

  // Остальное — не трогаем
  return ctx.originalFetch(input as any, init);
}

/**
 * Hook для использования тренировочного режима
 */
export const useTrainingMode = () => {
  const context = useContext(TrainingModeContext);
  if (!context) {
    throw new Error('useTrainingMode must be used within TrainingModeProvider');
  }
  return context;
};

/**
 * Provider компонент для тренировочного режима
 */
export const TrainingModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Инициализация: sessionStorage (primary) + localStorage bridge (secondary)
  const initialMode = useMemo(() => {
    const session = safeReadSessionMode();
    if (session) return true;

    // Если в sessionStorage пусто, но есть shared-флаг (новая вкладка) — включаем
    const shared = safeReadSharedMode();
    if (shared) {
      try {
        sessionStorage.setItem(TRAINING_MODE_KEY, 'true');
      } catch {
        // ignore
      }
      return true;
    }
    return false;
  }, []);

  const [isTrainingMode, setIsTrainingMode] = useState<boolean>(initialMode);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // refs для fetch-router
  const originalFetchRef = useRef<FetchFn | null>(null);
  const confirmationMapRef = useRef<Map<string, string>>(new Map());
  const confirmationStoreRef = useRef<Map<string, any>>(new Map());

  /**
   * Внутренняя установка режима с опциями (для storage-sync)
   */
  const setTrainingModeInternal = useCallback(
    (enabled: boolean, opts?: { syncSharedStorage?: boolean }) => {
      setIsTrainingMode(enabled);

      // sessionStorage — строго по ТЗ
      try {
        if (enabled) {
          sessionStorage.setItem(TRAINING_MODE_KEY, 'true');
        } else {
          sessionStorage.removeItem(TRAINING_MODE_KEY);
        }
      } catch {
        // ignore
      }

      // BackendService флаг (для всего кода, который уже использует BackendService)
      BackendService.setTrainingMode(enabled);

      // localStorage bridge (для сценария «новая вкладка»)
      if (opts?.syncSharedStorage !== false) {
        safeWriteSharedMode(enabled);
      }
    },
    []
  );

  /**
   * Включить тренировочный режим (публично)
   */
  const enableTrainingMode = useCallback(() => {
    setTrainingModeInternal(true, { syncSharedStorage: true });
  }, [setTrainingModeInternal]);

  /**
   * Выключить тренировочный режим (публично)
   */
  const disableTrainingMode = useCallback(() => {
    setTrainingModeInternal(false, { syncSharedStorage: true });
  }, [setTrainingModeInternal]);

  /**
   * Переключение режима
   */
  const toggleTrainingMode = useCallback(() => {
    setTrainingModeInternal(!isTrainingMode, { syncSharedStorage: true });
  }, [isTrainingMode, setTrainingModeInternal]);

  /**
   * Загрузка статистики из API
   */
  const loadStats = useCallback(async () => {
    if (!isTrainingMode) return;

    setIsLoading(true);
    try {
      const response = await BackendService.getTrainingStats();

      const lastActivityRaw = response?.lastActivity ?? null;
      const lastActivity = lastActivityRaw ? new Date(lastActivityRaw) : null;

      const normalized: TrainingStats = {
        totalRequests: Number(response?.totalRequests ?? 0) || 0,
        pendingRequests: Number(response?.pendingRequests ?? 0) || 0,
        approvedRequests: Number(response?.approvedRequests ?? 0) || 0,
        completedTests: Number(response?.completedTests ?? 0) || 0,
        rejectedRequests: Number(response?.rejectedRequests ?? 0) || 0,
        expiredRequests: Number(response?.expiredRequests ?? 0) || 0,
        totalSignals: Number(response?.totalSignals ?? 0) || 0,
        testSignals: Number(response?.testSignals ?? 0) || 0,
        distressSignals: Number(response?.distressSignals ?? 0) || 0,
        pollSignals: Number(response?.pollSignals ?? 0) || 0,
        lastActivity: lastActivity && !Number.isNaN(lastActivity.getTime()) ? lastActivity : null,
      };

      setStats(normalized);
    } catch (error) {
      console.error('❌ Ошибка загрузки статистики тренировочного режима:', error);
      setStats(initialStats);
    } finally {
      setIsLoading(false);
    }
  }, [isTrainingMode]);

  /**
   * Сброс тренировочных данных
   */
  const resetData = useCallback(async () => {
    setIsLoading(true);
    try {
      await BackendService.resetTrainingData();
      await loadStats();
      console.log('🗑️ Тренировочные данные сброшены');
    } finally {
      setIsLoading(false);
    }
  }, [loadStats]);

  /**
   * Заполнение демо-данными
   */
  const seedData = useCallback(async () => {
    setIsLoading(true);
    try {
      await BackendService.seedTrainingData();
      await loadStats();
      console.log('🌱 Демо-данные добавлены');
    } finally {
      setIsLoading(false);
    }
  }, [loadStats]);

  /**
   * 1) Синхронизируем BackendService на случай прямой установки isTrainingMode при старте
   */
  useEffect(() => {
    BackendService.setTrainingMode(isTrainingMode);
  }, [isTrainingMode]);

  /**
   * 2) Polling stats в training mode (для бейджа в шапке)
   */
  useEffect(() => {
    if (!isTrainingMode) {
      setStats(null);
      return;
    }

    loadStats();
    const interval = window.setInterval(() => loadStats(), 10_000); // 10 секунд
    return () => window.clearInterval(interval);
  }, [isTrainingMode, loadStats]);

  /**
   * 3) Глобальный fetch-router: включается только в training mode
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch.bind(window);
    }

    const originalFetch = originalFetchRef.current;
    if (!originalFetch) return;

    if (!isTrainingMode) {
      // restore
      window.fetch = originalFetch;
      return;
    }

    // patch
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      return routeFetchInTrainingMode(input, init, {
        originalFetch,
        confirmationToRequest: confirmationMapRef.current,
        confirmations: confirmationStoreRef.current,
      });
    };

    // cleanup (на всякий случай)
    return () => {
      window.fetch = originalFetch;
    };
  }, [isTrainingMode]);

  /**
   * 4) Cross-tab sync: реагируем на изменения shared-key
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (e: StorageEvent) => {
      if (e.key !== TRAINING_MODE_SHARED_KEY) return;

      const shouldEnable = safeReadSharedMode();
      if (shouldEnable !== isTrainingMode) {
        // Важно: при реакции на storage-event не перезаписываем shared storage обратно
        setTrainingModeInternal(shouldEnable, { syncSharedStorage: false });
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [isTrainingMode, setTrainingModeInternal]);

  // mockData для обратной совместимости
  const mockData =
    isTrainingMode && stats
      ? {
          requests: [],
          signals: [],
          terminals: [],
          stats,
        }
      : {};

  const value: TrainingModeContextType = {
    isTrainingMode,
    toggleTrainingMode,
    enableTrainingMode,
    disableTrainingMode,
    stats,
    loadStats,
    isLoading,
    resetData,
    seedData,
    mockData,
  };

  return <TrainingModeContext.Provider value={value}>{children}</TrainingModeContext.Provider>;
};

/**
 * Компонент визуального индикатора режима (опционально).
 * Можно не использовать, если бейдж в шапке уже отображается.
 */
export const TrainingModeIndicator: React.FC = () => {
  const { isTrainingMode, stats, isLoading } = useTrainingMode();

  if (!isTrainingMode) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        padding: '8px 16px',
        backgroundColor: '#ff9800',
        color: '#000',
        borderRadius: '4px',
        fontWeight: 'bold',
        fontSize: '14px',
        zIndex: 9999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <span style={{ fontSize: '18px' }}>🎓</span>
      <span>РЕЖИМ ТРЕНИРОВКИ</span>
      {isLoading && <span style={{ fontSize: '12px' }}>⏳</span>}
      {stats && !isLoading && (
        <span style={{ fontSize: '12px', opacity: 0.85 }}>
          ({stats.totalRequests} заявок, {stats.totalSignals} сигналов)
        </span>
      )}
    </div>
  );
};

export default TrainingModeContext;
